import axios from "axios";
import { type TagsToContent } from "../../../../interfaces";
import { type WithPagedDataV2, type GetPagedDataByIdRequestV3 } from "../../../../interfaces/getPagedDataRequest";
import { formatFiltersV2api, mapToV2Filters, serializeArrayAndFilterNullable } from "../../../../utils/queryUtils";
import { escapeTerm } from "../../../../utils/searchUtils";
import KnownHttpHeaders from "../../../Application/services/knownHttpHeaders";
import { v2FilterMap } from "../../../People/types";
import { type SurveyPayload, type UpdateContentPayload } from "../types/models";
import { type SaveTagsRequest } from "../types/requests";
import { type ISurveysDataService } from "../types/services";
import { type EngagementLineData, type EngagementData } from "../state/slices/surveyPerformanceSlice";
import { type SurveyQuestion, type SurveyPeopleDetails, type SurveyPerformanceAnswer } from "../types/state";
import { type BasePerformanceRequestFilterParams } from "../../Common/models";
import { CHART_PERIOD } from "hooks/useChartPeriodMeasure";

// For abort previous requests if filter changes
let topLevelRequests = new AbortController();
let answersAbortController = new AbortController();
let peopleDetailsAbortController = new AbortController();
let exportAbortController = new AbortController();

export const SURVEY_QUESTIONS_ENDPOINT = "/api/reports/v5/surveys/questions";
export const SURVEY_PEOPLE_DETAILS_ENDPOINT = "/api/reports/v5/surveys/people-details";
export const SURVEY_EXPORT_ENDPOINT = "/api/dataexport/v5/surveys/user-progress";
export const SURVEY_PERFORMANCE_QUESTIONS_ENDPOINT = "/api/reports/v5/surveys/performance-question";
export const SURVEY_PERFORMANCE_ANSWERS_ENDPOINT = "/api/reports/v5/surveys/performance-answer";

const surveysDataService: ISurveysDataService = {
  getPublishedSurveysByIds(ids: string[], showPurchased: boolean) {
    const config = {
      params: {
        showPurchased,
        filter: ids.map((x) => `(PrimaryId eq ${x})`).join(" or "),
      },
    };

    return axios.get("/api/assets/surveys/published", config);
  },

  updateSurveyAsync(data: SurveyPayload) {
    return axios.put(`/api/assets/surveys/${data.id}`, data);
  },

  updateContentAsync(data: UpdateContentPayload) {
    return axios.put(`/api/assets/surveys/${data.id}/questions`, data);
  },

  getSurveyAsync(id: number) {
    return axios.get(`/api/assets/surveys/${id}`);
  },

  getSurveyContentAsync(id: number) {
    return axios.get(`/api/assets/surveys/${id}/questions`);
  },

  getSurveyPublishedQuestions(ids: number[], abortController: AbortController) {
    return axios.get("/api/v4/assets/surveys/questions/published", {
      params: {
        ids,
      },
      paramsSerializer: serializeArrayAndFilterNullable,
      signal: abortController.signal,
    });
  },

  async getSurveyUsersAsyncV2<T>(entityId: number, request: WithPagedDataV2<T>) {
    const config = {
      params: request,
      paramsSerializer: serializeArrayAndFilterNullable,
    };

    const response = await axios.get(`/api/v4/content/survey/${entityId}/users`, config);

    return {
      items: response.data,
      count: parseInt(response.headers[KnownHttpHeaders.RecordsCount]),
    };
  },

  async getSurveyGroupsAsyncV2(request: GetPagedDataByIdRequestV3) {
    const { entityId, skip, top, term, filter } = request;

    const response = await axios.get(`/api/v4/content/survey/${entityId}/groups`, {
      params: {
        skip,
        top,
        ...formatFiltersV2api(mapToV2Filters(filter!, v2FilterMap)),
        term: escapeTerm(term),
      },
      paramsSerializer: serializeArrayAndFilterNullable,
    });

    return {
      items: response.data,
      count: parseInt(response.headers[KnownHttpHeaders.RecordsCount]),
    };
  },

  async deleteSurveyAsync(ids: number[]) {
    const result = await axios.delete("/api/assets/surveys", {
      data: { surveyIds: ids.map((id) => id.toString()) },
    });
    return result.data;
  },

  async duplicateSurveyAsync(ids: number[]) {
    const result = await axios.post("/api/assets/surveys/duplicate", { surveyIds: ids.map((id) => id.toString()) });
    return result.data;
  },

  getPublishersFilterOptionsAsync() {
    return axios.get("/api/assets/surveys/publishers");
  },

  getSurveysTagsAsync(showPurchased: boolean) {
    return axios.get("/api/v2/assets/surveys/tags", { params: { showPurchased } });
  },
  getSurveyDependenciesAsync(id: number) {
    return axios.get(`api/assets/surveys/${id}/dependencies`);
  },
  addSurveyTags(data: TagsToContent) {
    return axios.post(`/api/assets/surveys/tags`, data);
  },
  saveTags(request: SaveTagsRequest) {
    return axios.put(`/api/assets/surveys/${request.id}/tags`, {
      tagType: request.tagType,
      tags: request.tags,
    });
  },

  // Performance tab requests
  async getSurveyEngagementLine(surveyId: number, filterParams: BasePerformanceRequestFilterParams) {
    const response = await axios.get<EngagementLineData>("/api/reports/v4/surveys/linechart", {
      params: {
        surveyId,
        period: CHART_PERIOD.DAY,
        ...filterParams,
      },
      signal: topLevelRequests.signal,
      paramsSerializer: serializeArrayAndFilterNullable,
    });
    return response.data;
  },

  async getSurveyQuestionsData(surveyId: number, filterParams: BasePerformanceRequestFilterParams) {
    const response = await axios.get<SurveyQuestion[]>(SURVEY_QUESTIONS_ENDPOINT, {
      params: {
        surveyId,
        ...filterParams,
      },
      signal: topLevelRequests.signal,
      paramsSerializer: serializeArrayAndFilterNullable,
    });
    return response.data;
  },

  async getSurveyEngagement(surveyId: number, filterParams: BasePerformanceRequestFilterParams) {
    const response = await axios.get<EngagementData>("/api/reports/v4/surveys/engagement", {
      params: {
        surveyId,
        ...filterParams,
      },
      signal: topLevelRequests.signal,
      paramsSerializer: serializeArrayAndFilterNullable,
    });
    return response.data;
  },

  async getSurveyQuestionDetails(
    surveyId: number,
    questionId: string,
    filterParams: BasePerformanceRequestFilterParams,
  ) {
    const response = await axios.get<[SurveyQuestion]>(SURVEY_PERFORMANCE_QUESTIONS_ENDPOINT, {
      params: { surveyId, questionId, ...filterParams },
      paramsSerializer: serializeArrayAndFilterNullable,
    });
    return response.data;
  },

  async getSurveyAnswerDetails(
    surveyId: number,
    questionId: string,
    answerId: string,
    filterParams: BasePerformanceRequestFilterParams,
  ): Promise<SurveyPerformanceAnswer> {
    answersAbortController.abort();
    answersAbortController = new AbortController();

    const response = await axios.get<[SurveyPerformanceAnswer]>(SURVEY_PERFORMANCE_ANSWERS_ENDPOINT, {
      params: { surveyId, questionId, answerId, ...filterParams },
      signal: answersAbortController.signal,
      paramsSerializer: serializeArrayAndFilterNullable,
    });

    return response.data[0];
  },

  async getSurveyPeopleDetails(
    surveyId: number,
    filterParams: BasePerformanceRequestFilterParams,
  ): Promise<SurveyPeopleDetails[]> {
    peopleDetailsAbortController.abort();
    peopleDetailsAbortController = new AbortController();

    const response = await axios.get<SurveyPeopleDetails[]>(SURVEY_PEOPLE_DETAILS_ENDPOINT, {
      params: { surveyId, ...filterParams },
      signal: peopleDetailsAbortController.signal,
      paramsSerializer: serializeArrayAndFilterNullable,
    });

    return response.data;
  },

  async getExport(surveyId: number, filterParams: BasePerformanceRequestFilterParams) {
    exportAbortController.abort();
    exportAbortController = new AbortController();
    return axios.get(SURVEY_EXPORT_ENDPOINT, {
      responseType: "blob",
      params: {
        surveyId,
        ...filterParams,
      },
      signal: exportAbortController.signal,
      paramsSerializer: serializeArrayAndFilterNullable,
    });
  },

  // On filter change
  cancelTopLevelPerformanceRequests() {
    topLevelRequests.abort();
    topLevelRequests = new AbortController();
  },

  cancelSurveyAnswerDetails() {
    answersAbortController.abort();
  },

  changeVisibility(ids: number[], visibility: boolean) {
    return axios.post("/api/assets/surveys/visibility", {
      ids,
      visibility,
    });
  },
};

export default surveysDataService;
